import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import useStore from '../store/useStore';
import { showDesktopNotification } from '../services/desktopNotificationService';
import { notifications as notificationsApi } from '../services/api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL ||
  (typeof window !== 'undefined' && window.location.port === '3000' ? 'http://localhost:5000' : null);

let socket = null;

export function useSocket() {
  const token = useStore((s) => s.token);
  const setBotStatus = useStore((s) => s.setBotStatus);
  const setDashboardData = useStore((s) => s.setDashboardData);
  const setMarketData = useStore((s) => s.setMarketData);
  const addNotification = useStore((s) => s.addNotification);
  const maxSeenIdRef = useRef(0);

  useEffect(() => {
    if (!token) return;

    if (!SOCKET_URL) {
      socket = null;
    } else {
      socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => console.log('Socket connected'));
    socket.on('disconnect', () => console.log('Socket disconnected'));

    socket.on('notification:new', (notification) => {
      if (notification?.id && notification.id > maxSeenIdRef.current) {
        maxSeenIdRef.current = notification.id;
      }
      if (addNotification) addNotification(notification);
      showDesktopNotification(notification);
    });

    socket.on('bot_status_change', (data) => {
      setBotStatus(data);
      const msg = data.isRunning ? `Bot started (${data.mode} mode)` : `Bot stopped: ${data.reason || ''}`;
      const notifObj = { title: 'Bot Status Update', type: data.isRunning ? 'success' : 'warning', message: msg, priority: 'HIGH', time: new Date() };
      addNotification(notifObj);
      showDesktopNotification(notifObj);
    });

    socket.on('dashboard_update', (data) => setDashboardData(data));

    socket.on('market_data', (data) => {
      setMarketData(data.symbol, data);
    });

    socket.on('position_update', (data) => {
      const notifObj = { title: 'Position Update', type: 'info', message: `Position updated: ${data.id}`, symbol: data.symbol, orderId: data.id, time: new Date() };
      addNotification(notifObj);
    });

    socket.on('live_position_update', (data) => {
      const notifObj = { title: 'Live Position Update', type: 'info', message: `Live position updated: ${data.id}`, symbol: data.symbol, orderId: data.id, time: new Date() };
      addNotification(notifObj);
    });
    }

    // Fallback unread database log poller (checks every 10s for database record insertions)
    const checkUnreadDbLogs = async () => {
      try {
        const res = await notificationsApi.list({ isRead: 'false', limit: 5 });
        const list = res.data?.data || [];
        for (const item of list) {
          if (item.id && item.id > maxSeenIdRef.current) {
            maxSeenIdRef.current = item.id;
            if (addNotification) addNotification(item);
            showDesktopNotification(item);
          }
        }
      } catch (e) { /* ignore polling errors */ }
    };

    checkUnreadDbLogs();
    const pollInterval = setInterval(checkUnreadDbLogs, 10000);

    return () => {
      clearInterval(pollInterval);
      if (socket) { socket.disconnect(); socket = null; }
    };
  }, [token]);

  return socket;
}

export function getSocket() { return socket; }
